import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import * as XLSX from "xlsx";
import AuthService from "../services/auth.service";
import DataService from "../services/data.service";

const pad = (value) => String(value).padStart(2, "0");

const toDateTimeLocalValue = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;

const getDefaultReportPeriod = () => {
  const to = new Date();
  to.setMinutes(0, 0, 0);

  const from = new Date(to);
  from.setDate(from.getDate() - 1);

  return {
    fromDate: toDateTimeLocalValue(from),
    toDate: toDateTimeLocalValue(to),
  };
};

const isValidDateTimeValue = (value) => {
  if (!value) return false;
  return !Number.isNaN(new Date(value).getTime());
};

const toReportDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date;
};

const formatFileDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value ?? "").replace("T", " ");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}${pad(date.getMinutes())}`;
};

const cleanFilePart = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, " ");

const cleanSheetName = (value, fallback) => {
  const name = cleanFilePart(value || fallback)
    .replace(/[[\]]/g, "-")
    .slice(0, 31)
    .trim();
  return name || fallback;
};

const formatChartTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const getHourTimestamp = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setMinutes(0, 0, 0);
  return date.getTime();
};

const lampSeries = [
  { key: "lamp1", label: "Lampe 1", color: "var(--color-400)" },
  { key: "lamp2", label: "Lampe 2", color: "var(--color-300)" },
  { key: "lamp3", label: "Lampe 3", color: "var(--color-500)" },
  { key: "lamp4", label: "Lampe 4", color: "var(--app-warning)" },
  { key: "lamp5", label: "Lampe 5", color: "var(--app-ok)" },
  { key: "lamp6", label: "Lampe 6", color: "var(--app-alarm)" },
];

const groupRowsBySystem = (rows) => {
  const groups = rows.reduce((nextGroups, row) => {
    const key = `${row.systemId ?? row.IMEI}`;
    if (!nextGroups.has(key)) {
      nextGroups.set(key, {
        systemId: row.systemId,
        merde: row.merde || row.IMEI || "Merde",
        IMEI: row.IMEI,
        rows: [],
      });
    }

    const timestamp = getHourTimestamp(row.time);
    if (timestamp == null) return nextGroups;

    nextGroups.get(key).rows.push({
      timestamp,
      label: formatChartTime(timestamp),
      lamp1: row.lamps?.[0] ?? null,
      lamp2: row.lamps?.[1] ?? null,
      lamp3: row.lamps?.[2] ?? null,
      lamp4: row.lamps?.[3] ?? null,
      lamp5: row.lamps?.[4] ?? null,
      lamp6: row.lamps?.[5] ?? null,
    });

    return nextGroups;
  }, new Map());

  return [...groups.values()]
    .map((group) => {
      const sortedRows = group.rows.sort((a, b) => a.timestamp - b.timestamp);
      const activeLampKeys = lampSeries
        .filter((series) => sortedRows.some((row) => row[series.key] != null))
        .map((series) => series.key);

      return {
        ...group,
        activeLampKeys,
        rows: sortedRows,
      };
    })
    .sort((a, b) =>
      (a.merde ?? "").localeCompare(b.merde ?? "", "nb", {
        numeric: true,
        sensitivity: "base",
      })
    );
};

const PercentTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const values = payload.filter((entry) => entry.value != null);

  return (
    <div className="report-chart-tooltip">
      <strong>{formatChartTime(label)}</strong>
      {values.length > 0 ? (
        values.map((entry) => (
          <div key={entry.dataKey}>
            {entry.name}: {Number(entry.value).toFixed(1)}%
          </div>
        ))
      ) : (
        <div>Ingen data</div>
      )}
    </div>
  );
};

const downloadExcelSheet = (report) => {
  const rowsByMerde = report.rows.reduce((groups, row) => {
    const key = row.merde || row.IMEI || "Merde";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
    return groups;
  }, new Map());

  const workbook = XLSX.utils.book_new();

  [...rowsByMerde.entries()].forEach(([merde, rows], index) => {
    const sheetRows = rows.map((row) => ({
      Tid: toReportDate(row.time),
      "Lampe 1": row.lamps[0] ?? null,
      "Lampe 2": row.lamps[1] ?? null,
      "Lampe 3": row.lamps[2] ?? null,
      "Lampe 4": row.lamps[3] ?? null,
      "Lampe 5": row.lamps[4] ?? null,
      "Lampe 6": row.lamps[5] ?? null,
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows, {
      header: ["Tid", "Lampe 1", "Lampe 2", "Lampe 3", "Lampe 4", "Lampe 5", "Lampe 6"],
      cellDates: true,
    });

    worksheet["!cols"] = [
      { wch: 18 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
    ];

    Object.keys(worksheet).forEach((cellRef) => {
      if (cellRef.startsWith("!") || cellRef.match(/[A-Z]+1$/)) return;
      if (cellRef[0] === "A") {
        worksheet[cellRef].z = "yyyy-mm-dd hh:mm";
        return;
      }
      if (typeof worksheet[cellRef].v === "number") {
        worksheet[cellRef].z = "0.0%";
        worksheet[cellRef].v = worksheet[cellRef].v / 100;
      }
    });

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      cleanSheetName(merde, `Merde ${index + 1}`)
    );
  });

  const clusterName = cleanFilePart(report.cluster?.nick ?? "Lokasjon");
  const from = formatFileDateTime(report.fromDate);
  const to = formatFileDateTime(report.toDate);
  XLSX.writeFile(workbook, `${clusterName} (${from} - ${to}).xlsx`);
};

export default function ModeratorHourlyReport() {
  const [clusters, setClusters] = useState([]);
  const [clusterId, setClusterId] = useState("");
  const [selectedSystems, setSelectedSystems] = useState([]);
  const [fromDate, setFromDate] = useState(() => getDefaultReportPeriod().fromDate);
  const [toDate, setToDate] = useState(() => getDefaultReportPeriod().toDate);
  const [loadingAction, setLoadingAction] = useState(null);
  const [message, setMessage] = useState("");
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    if (!currentUser) return;

    DataService.getClusters(currentUser.id).then(
      (response) => {
        const nextClusters = response.data ?? [];
        setClusters(nextClusters);
        if (nextClusters.length > 0) {
          const firstCluster = nextClusters[0];
          setClusterId(String(firstCluster.id));
          setSelectedSystems((firstCluster.systems ?? []).map((system) => system.id));
        }
      },
      () => {
        setMessage("Kunne ikke hente lokasjoner/systemer.");
      }
    );
  }, []);

  const selectedCluster = useMemo(
    () => clusters.find((cluster) => String(cluster.id) === String(clusterId)),
    [clusters, clusterId]
  );

  const systems = selectedCluster?.systems ?? [];

  const toggleSystem = (systemId) => {
    setSelectedSystems((prev) =>
      prev.includes(systemId)
        ? prev.filter((id) => id !== systemId)
        : [...prev, systemId]
    );
  };

  const handleClusterChange = (event) => {
    const nextClusterId = event.target.value;
    const nextCluster = clusters.find((cluster) => String(cluster.id) === nextClusterId);
    setClusterId(nextClusterId);
    setSelectedSystems((nextCluster?.systems ?? []).map((system) => system.id));
    setReportData(null);
  };

  const fetchReport = useCallback(async (action, showEmptyMessage = true) => {
    setMessage("");

    if (!clusterId || selectedSystems.length === 0) {
      if (showEmptyMessage) setMessage("Velg lokasjon og minst ett system.");
      return null;
    }

    let nextFromDate = fromDate;
    let nextToDate = toDate;

    if (!isValidDateTimeValue(nextFromDate) || !isValidDateTimeValue(nextToDate)) {
      const fallbackPeriod = getDefaultReportPeriod();
      nextFromDate = fallbackPeriod.fromDate;
      nextToDate = fallbackPeriod.toDate;
      setFromDate(nextFromDate);
      setToDate(nextToDate);
    }

    setLoadingAction(action);
    try {
      const response = await DataService.getHourlyLampReport({
        clusterId: Number(clusterId),
        systemIds: selectedSystems,
        fromDate: nextFromDate,
        toDate: nextToDate,
      });

      if (!response.data?.rows?.length) {
        if (showEmptyMessage) setMessage("Ingen data funnet for valgt periode.");
        setReportData(null);
        return null;
      }

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Kunne ikke lage rapport.";
      setMessage(errorMessage);
      setReportData(null);
      return null;
    } finally {
      setLoadingAction(null);
    }
  }, [clusterId, selectedSystems, fromDate, toDate]);

  const createReport = async () => {
    const report = await fetchReport("download");
    if (!report) return;

    downloadExcelSheet(report);
    setMessage(`Rapport laget med ${report.rows.length} rader.`);
  };

  const chartGroups = useMemo(
    () => (reportData?.rows ? groupRowsBySystem(reportData.rows) : []),
    [reportData]
  );

  const chartDomain = useMemo(() => {
    const from = getHourTimestamp(fromDate);
    const to = getHourTimestamp(toDate);
    return from != null && to != null && from <= to ? [from, to] : ["auto", "auto"];
  }, [fromDate, toDate]);

  useEffect(() => {
    if (!clusterId || selectedSystems.length === 0) {
      setReportData(null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      const report = await fetchReport("charts", false);
      if (report) {
        setReportData(report);
        setMessage(`Viser ${report.rows.length} timeverdier.`);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [clusterId, selectedSystems, fromDate, toDate, fetchReport]);

  return (
    <span className="viewcard41">
      <div className="moderator-report">
        <h2>Driftsrapport</h2>

        <div className="report-controls">
          <label>
            Lokasjon
            <select value={clusterId} onChange={handleClusterChange}>
              {clusters.map((cluster) => (
                <option key={cluster.id} value={cluster.id}>
                  {cluster.nick} - {cluster.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Fra
            <input
              type="datetime-local"
              value={fromDate}
              onChange={(event) => {
                setFromDate(event.target.value);
                setReportData(null);
              }}
            />
          </label>

          <label>
            Til
            <input
              type="datetime-local"
              value={toDate}
              onChange={(event) => {
                setToDate(event.target.value);
                setReportData(null);
              }}
            />
          </label>
        </div>

        <div className="report-system-list">
          {systems.map((system) => (
            <label key={system.id} className="report-system-option">
              <input
                type="checkbox"
                checked={selectedSystems.includes(system.id)}
                onChange={() => {
                  toggleSystem(system.id);
                  setReportData(null);
                }}
              />
              <span>
                {system.nick} <span className="imei">({system.IMEI})</span>
              </span>
            </label>
          ))}
        </div>

        <div className="report-actions">
          <button className="report-action" onClick={createReport} disabled={loadingAction}>
            {loadingAction === "download" ? "Lager rapport..." : "Last ned driftsrapport"}
          </button>
        </div>

        {message ? <div className="report-message">{message}</div> : null}

        {chartGroups.length > 0 ? (
          <div className="report-chart-grid">
            {chartGroups.map((group) => (
              <section key={`${group.systemId}-${group.IMEI}`} className="report-chart-card">
                <header>
                  <h3>{group.merde}</h3>
                  <span>{group.IMEI}</span>
                </header>

                <div className="report-chart">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={group.rows} margin={{ top: 12, right: 18, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke="var(--app-border)" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        type="number"
                        domain={chartDomain}
                        scale="time"
                        tickFormatter={formatChartTime}
                        tick={{ fill: "var(--app-text-muted)", fontSize: 11 }}
                        minTickGap={26}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                        tick={{ fill: "var(--app-text-muted)", fontSize: 11 }}
                        width={42}
                      />
                      <Tooltip content={<PercentTooltip />} />
                      <Legend wrapperStyle={{ color: "var(--app-text-muted)" }} />
                      {lampSeries
                        .filter((series) => group.activeLampKeys.includes(series.key))
                        .map((series) => (
                          <Bar
                            key={series.key}
                            dataKey={series.key}
                            name={series.label}
                            fill={series.color}
                            minPointSize={3}
                            radius={[2, 2, 0, 0]}
                            isAnimationActive={false}
                          />
                        ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            ))}
          </div>
        ) : null}
      </div>
    </span>
  );
}
